import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Copy, ExternalLink, FolderInput, Globe, Link2, Lock, MoreHorizontal, Pencil, RotateCcw, Star, Trash2, Users } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { Menu, type MenuEntry } from '@/components/ui/Menu';
import { ConfirmDialog, PromptDialog } from '@/components/ui/PromptDialog';
import { t, timeAgo } from '@/i18n';
import { cn } from '@/lib/cn';
import type { Board } from '@/lib/types';
import { useBoardActions, type Project } from '@/lib/queries';
import { BoardThumb } from './BoardThumb';

const VIS_ICON = { private: Lock, workspace: Users, link: Link2, public: Globe } as const;
const VIS_LABEL = { private: 'share.private', workspace: 'share.workspace', link: 'share.link', public: 'share.public' } as const;

function useBoardMenu(board: Board, projects: Project[], trash: boolean) {
  const actions = useBoardActions();
  const navigate = useNavigate();
  const [renameOpen, setRenameOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const canEdit = board.role === 'owner' || board.role === 'editor';

  const items: MenuEntry[] = trash
    ? [
        { value: 'restore', label: t('dashboard.restore'), icon: <RotateCcw />, onSelect: () => actions.restore.mutate(board.id) },
        { value: 'purge', label: t('dashboard.deleteForever'), icon: <Trash2 />, danger: true, onSelect: () => setPurgeOpen(true) },
      ]
    : [
        { value: 'open', label: t('dashboard.open'), icon: <ExternalLink />, onSelect: () => navigate(`/board/${board.id}`) },
        { value: 'fav', label: board.isFavorite ? t('dashboard.removeFavorite') : t('dashboard.addFavorite'), icon: <Star />, onSelect: () => actions.favorite.mutate({ id: board.id, on: !board.isFavorite }) },
        ...(canEdit ? [{ value: 'rename', label: t('dashboard.rename'), icon: <Pencil />, onSelect: () => setRenameOpen(true) }] : []),
        { value: 'dup', label: t('dashboard.duplicate'), icon: <Copy />, onSelect: () => actions.duplicate.mutate({ id: board.id }) },
        ...(canEdit && projects.length
          ? ([
              'separator',
              { group: t('dashboard.moveTo') },
              { value: 'p-none', label: t('dashboard.noProject'), icon: <FolderInput />, disabled: !board.projectId, onSelect: () => actions.move.mutate({ id: board.id, projectId: null }) },
              ...projects.slice(0, 12).map((p) => ({
                value: `p-${p.id}`,
                label: p.name,
                icon: <FolderInput />,
                disabled: board.projectId === p.id,
                onSelect: () => actions.move.mutate({ id: board.id, projectId: p.id }),
              })),
            ] as MenuEntry[])
          : []),
        ...(board.role === 'owner' ? (['separator', { value: 'trash', label: t('dashboard.toTrash'), icon: <Trash2 />, danger: true, onSelect: () => actions.trash.mutate(board.id) }] as MenuEntry[]) : []),
      ];

  const dialogs = (
    <>
      <PromptDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title={t('dashboard.rename')}
        label={t('dashboard.name')}
        initial={board.name}
        onSubmit={(name) => actions.rename.mutateAsync({ id: board.id, name })}
      />
      <ConfirmDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        title={t('dashboard.deleteForever')}
        text={t('dashboard.deleteForeverConfirm')}
        confirmLabel={t('dashboard.deleteForever')}
        onConfirm={() => actions.purge.mutateAsync(board.id)}
      />
    </>
  );
  return { items, dialogs, actions };
}

function MoreButton({ items, className }: { items: MenuEntry[]; className?: string }) {
  return (
    <Menu
      side="bottom"
      align="end"
      items={items}
      trigger={
        <button
          type="button"
          aria-label={t('common.more')}
          className={cn('inline-flex size-8 items-center justify-center rounded-control text-fg-muted hover:bg-hover hover:text-fg [&_svg]:size-4', className)}
        >
          <MoreHorizontal />
        </button>
      }
    />
  );
}

export function BoardCard({ board, projects, trash = false, index = 0 }: { board: Board; projects: Project[]; trash?: boolean; index?: number }) {
  const { items, dialogs, actions } = useBoardMenu(board, projects, trash);
  const reduce = useReducedMotion();
  const VisIcon = VIS_ICON[board.visibility];
  return (
    <>
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 32, delay: Math.min(index, 12) * 0.025 }}
        className="group relative rounded-panel bg-surface shadow-card transition-[box-shadow,transform] duration-200 focus-within:shadow-float hover:-translate-y-0.5 hover:shadow-float"
      >
        {/* Ссылка растянута на всю карточку; кнопки лежат поверх неё. */}
        {!trash && <Link to={'/board/' + board.id} className="absolute inset-0 z-0 rounded-panel outline-none focus-visible:ring-2 focus-visible:ring-accent" aria-label={board.name} />}
        <div className="pointer-events-none relative">
          <BoardThumb id={board.id} src={board.thumbnailUrl} className="aspect-[16/10] w-full rounded-t-panel" />
        </div>
        {!trash && (
          <button
            type="button"
            aria-label={board.isFavorite ? t('dashboard.removeFavorite') : t('dashboard.addFavorite')}
            aria-pressed={board.isFavorite}
            onClick={() => actions.favorite.mutate({ id: board.id, on: !board.isFavorite })}
            className={cn(
              'absolute top-2.5 right-2.5 z-10 inline-flex size-8 items-center justify-center rounded-full bg-surface/90 shadow-card backdrop-blur transition-all',
              board.isFavorite ? 'text-amber-500 opacity-100' : 'text-fg-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            )}
          >
            <Star className={cn('size-4', board.isFavorite && 'fill-current')} />
          </button>
        )}
        <div className="pointer-events-none relative flex items-start gap-2 p-3.5 pr-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold">{board.name}</h3>
            <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-fg-subtle">
              <span title={t(VIS_LABEL[board.visibility])}>
                <VisIcon className="size-3" aria-hidden />
              </span>
              {trash ? t('dashboard.deleted', { time: timeAgo(board.deletedAt ?? board.updatedAt) }) : t('dashboard.updated', { time: timeAgo(board.contentUpdatedAt ?? board.updatedAt) })}
            </p>
          </div>
          <div className="pointer-events-auto relative z-10">
            <MoreButton items={items} className="opacity-60 group-hover:opacity-100 focus-visible:opacity-100" />
          </div>
        </div>
      </motion.div>
      {dialogs}
    </>
  );
}

export function BoardRow({ board, projects, trash = false }: { board: Board; projects: Project[]; trash?: boolean }) {
  const { items, dialogs } = useBoardMenu(board, projects, trash);
  const VisIcon = VIS_ICON[board.visibility];
  return (
    <>
      <div className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-control px-2 py-1.5 hover:bg-hover sm:grid-cols-[minmax(0,1fr)_160px_140px_auto]">
        <Link to={trash ? '#' : `/board/${board.id}`} className="flex min-w-0 items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-accent">
          <BoardThumb id={board.id} src={board.thumbnailUrl} className="h-10 w-16 shrink-0 rounded-md" />
          <span className="truncate text-sm font-medium">{board.name}</span>
          {board.isFavorite && <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-400" aria-label={t('dashboard.favorites')} />}
        </Link>
        <span className="hidden min-w-0 items-center gap-2 text-xs text-fg-muted sm:flex">
          {board.owner && <Avatar name={board.owner.name} src={board.owner.avatarUrl} seed={board.owner.id} size={20} />}
          <span className="truncate">{board.owner?.name}</span>
        </span>
        <span className="hidden items-center gap-1.5 text-xs text-fg-subtle sm:flex">
          <VisIcon className="size-3" aria-hidden />
          {timeAgo(trash ? (board.deletedAt ?? board.updatedAt) : (board.contentUpdatedAt ?? board.updatedAt))}
        </span>
        <MoreButton items={items} className="opacity-60 group-hover:opacity-100" />
      </div>
      {dialogs}
    </>
  );
}

export function BoardCardSkeleton() {
  return (
    <div className="rounded-panel bg-surface shadow-card">
      <div className="skeleton aspect-[16/10] rounded-t-panel" />
      <div className="space-y-2 p-3.5">
        <div className="skeleton h-3.5 w-2/3 rounded" />
        <div className="skeleton h-3 w-1/3 rounded" />
      </div>
    </div>
  );
}
